import DOMPurify from 'dompurify'
import { Marked } from 'marked'
import { expandMarkdownHighlights } from './markdownHighlights'
import { SOFT_LINE_BREAK_PLACEHOLDER_ATTRIBUTE } from './editorDom'

export { expandMarkdownHighlights } from './markdownHighlights'

const editorMarked = new Marked({
  gfm: true,
  breaks: true,
})

const BLOCK_TAGS = new Set([
  'BLOCKQUOTE',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HR',
  'OL',
  'P',
  'PRE',
  'UL',
])

export const NOTE_LINK_OPEN_ATTRIBUTE = 'data-note-link-open'
export const NOTE_LINK_CARET_ATTRIBUTE = 'data-note-link-caret'
/** Controls the editor adds inside a link row (Copy, Edit). Text-free, never part of the Markdown. */
export const NOTE_LINK_GENERATED_ATTRIBUTE = 'data-note-link-generated'

const NOTE_LINK_CARET_CHARACTER = '\u200B'
const INLINE_TAG_LINK_LABEL = /^#([a-z0-9][a-z0-9_-]*)$/i
const INLINE_TAG_MARKDOWN_LINK = /\[(#[a-z0-9][a-z0-9_-]*)([\t \u00A0]*)\]\(#([a-z0-9][a-z0-9_-]*)\)/gi

function inlineTagForLink(link: HTMLAnchorElement): string | null {
  const label = (link.textContent ?? '').trim()
  const labelTag = INLINE_TAG_LINK_LABEL.exec(label)?.[1]
  const hrefTag = INLINE_TAG_LINK_LABEL.exec(link.getAttribute('href') ?? '')?.[1]
  return labelTag && hrefTag && labelTag.toLocaleLowerCase() === hrefTag.toLocaleLowerCase()
    ? labelTag.toLocaleLowerCase()
    : null
}

function unwrapInlineTagLinks(root: ParentNode): string[] {
  const tags: string[] = []
  for (const link of root.querySelectorAll<HTMLAnchorElement>('a[href]')) {
    const tag = inlineTagForLink(link)
    if (!tag) continue
    tags.push(tag)
    link.replaceWith(...link.childNodes)
  }
  return [...new Set(tags)]
}

export function normalizeInlineTagLinks(markdown: string): { markdown: string; tags: string[] } {
  const tags: string[] = []
  const normalized = markdown.replace(
    INLINE_TAG_MARKDOWN_LINK,
    (source, label: string, trailingSpace: string, hrefTag: string) => {
      const labelTag = label.slice(1)
      if (labelTag.toLocaleLowerCase() !== hrefTag.toLocaleLowerCase()) return source
      tags.push(labelTag.toLocaleLowerCase())
      return `${label}${trailingSpace}`
    },
  )
  return { markdown: normalized, tags: [...new Set(tags)] }
}

function isOpenableWebLink(href: string): boolean {
  try {
    const url = new URL(href, document.baseURI)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Adds a separate, non-editable open action after each authored web link. The
 * action is deliberately ignored by Markdown serialization so presentation UI
 * never leaks into the saved note body.
 */
export function ensureFormattedNoteLinkActions(root: ParentNode): void {
  for (const action of root.querySelectorAll<HTMLAnchorElement>(`a[${NOTE_LINK_OPEN_ATTRIBUTE}]`)) {
    const caret = action.previousElementSibling
    const source = caret?.previousElementSibling
    if (
      !(caret instanceof HTMLSpanElement)
      || !caret.hasAttribute(NOTE_LINK_CARET_ATTRIBUTE)
      || !(source instanceof HTMLAnchorElement)
      || source.hasAttribute(NOTE_LINK_OPEN_ATTRIBUTE)
      || source.getAttribute('href') !== action.getAttribute('href')
    ) action.remove()
  }

  for (const caret of root.querySelectorAll<HTMLSpanElement>(`span[${NOTE_LINK_CARET_ATTRIBUTE}]`)) {
    const source = caret.previousElementSibling
    const action = caret.nextElementSibling
    if (
      !(source instanceof HTMLAnchorElement)
      || source.hasAttribute(NOTE_LINK_OPEN_ATTRIBUTE)
      || !(action instanceof HTMLAnchorElement)
      || !action.hasAttribute(NOTE_LINK_OPEN_ATTRIBUTE)
    ) caret.remove()
  }

  for (const link of root.querySelectorAll<HTMLAnchorElement>(`a[href]:not([${NOTE_LINK_OPEN_ATTRIBUTE}])`)) {
    const href = link.getAttribute('href') ?? ''
    if (!isOpenableWebLink(href)) continue
    if (link.closest('[data-link-preview-source="true"]')) {
      const caret = link.nextElementSibling
      if (caret instanceof HTMLSpanElement && caret.hasAttribute(NOTE_LINK_CARET_ATTRIBUTE)) {
        const action = caret.nextElementSibling
        if (action instanceof HTMLAnchorElement && action.hasAttribute(NOTE_LINK_OPEN_ATTRIBUTE)) {
          action.remove()
        }
        caret.remove()
      }
      continue
    }
    let caret = link.nextElementSibling
    if (!(caret instanceof HTMLSpanElement) || !caret.hasAttribute(NOTE_LINK_CARET_ATTRIBUTE)) {
      caret = document.createElement('span')
      caret.className = 'formatted-note-link-caret'
      caret.setAttribute(NOTE_LINK_CARET_ATTRIBUTE, 'true')
      caret.append(document.createTextNode(NOTE_LINK_CARET_CHARACTER))
      link.after(caret)
    } else if (!caret.textContent) {
      caret.append(document.createTextNode(NOTE_LINK_CARET_CHARACTER))
    }

    const existingAction = caret.nextElementSibling
    let action: HTMLAnchorElement
    if (existingAction instanceof HTMLAnchorElement && existingAction.hasAttribute(NOTE_LINK_OPEN_ATTRIBUTE)) {
      action = existingAction
    } else {
      action = document.createElement('a')
      caret.after(action)
    }
    action.className = 'formatted-note-link-open'
    action.setAttribute(NOTE_LINK_OPEN_ATTRIBUTE, 'true')
    action.setAttribute('href', href)
    action.setAttribute('target', '_blank')
    action.setAttribute('rel', 'noreferrer noopener')
    action.setAttribute('aria-label', `Open link in new tab: ${(link.textContent ?? href).trim() || href}`)
    action.setAttribute('title', 'Open link in new tab')
    action.contentEditable = 'false'
    let unit = link.parentElement
    if (!unit?.hasAttribute('data-note-link-unit')) {
      unit = document.createElement('span')
      unit.className = 'formatted-note-link-unit'
      unit.setAttribute('data-note-link-unit', 'true')
      link.before(unit)
      unit.append(link, caret, action)
    }
    // Older Enter handling persisted a trailing break inside the title. Keep
    // that authored newline, but place it after the complete link and action.
    const tail = document.createDocumentFragment()
    while (link.lastChild instanceof HTMLBRElement
      || (link.lastChild instanceof Text && /^\s+$/.test(link.lastChild.data))) {
      tail.prepend(link.lastChild!)
    }
    if (tail.childNodes.length) unit.after(tail)
  }
  for (const unit of root.querySelectorAll('[data-note-link-unit]')) {
    if (!unit.querySelector(`a:not([${NOTE_LINK_OPEN_ATTRIBUTE}])`)) {
      for (const generated of unit.querySelectorAll(`[${NOTE_LINK_GENERATED_ATTRIBUTE}]`)) generated.remove()
      unit.replaceWith(...unit.childNodes)
    }
  }
}

export function markdownToFormattedNoteContainer(markdown: string): HTMLDivElement {
  const container = document.createElement('div')
  if (!markdown.trim()) return container
  const normalizedMarkdown = normalizeInlineTagLinks(markdown).markdown
  const parsed = editorMarked.parse(expandMarkdownHighlights(normalizedMarkdown), {
    async: false,
  }) as string
  const sanitized = DOMPurify.sanitize(parsed, {
    ALLOWED_TAGS: [
      'a',
      'b',
      'blockquote',
      'br',
      'code',
      'del',
      'em',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'hr',
      'i',
      'input',
      'li',
      'mark',
      'ol',
      'p',
      'pre',
      's',
      'strong',
      'u',
      'ul',
    ],
    ALLOWED_ATTR: ['checked', 'class', 'href', 'start', 'title', 'type'],
  })
  container.innerHTML = sanitized

  // Marked emits loose task items as <li><p><input>…</p></li>. Canonicalize the
  // control as the final direct child so editable text starts at the same DOM
  // boundary as ordinary bullet and numbered items.
  for (const input of container.querySelectorAll<HTMLInputElement>('li input[type="checkbox"]')) {
    const item = input.closest('li')
    const paragraph = input.parentElement?.tagName === 'P' ? input.parentElement : null
    if (!item) continue
    if (paragraph?.parentElement === item) {
      while (paragraph.firstChild) item.insertBefore(paragraph.firstChild, paragraph)
      paragraph.remove()
    }
    item.append(input)
  }

  // Markdown merges adjacent bullet and task lists into one loose UL. Split it
  // back into contiguous semantic groups so bullet rows never inherit checklist UI.
  for (const list of [...container.querySelectorAll<HTMLUListElement>('ul')]) {
    const items = [...list.children].filter((child): child is HTMLLIElement => child.tagName === 'LI')
    const kinds = items.map((item) => Boolean(item.querySelector(':scope > input[type="checkbox"]')))
    if (new Set(kinds).size < 2) {
      if (kinds[0]) list.setAttribute('data-checklist', 'true')
      continue
    }

    let currentList: HTMLUListElement | null = null
    let currentKind: boolean | null = null
    for (const [index, item] of items.entries()) {
      const kind = kinds[index]!
      if (!currentList || kind !== currentKind) {
        currentList = list.cloneNode(false) as HTMLUListElement
        if (kind) currentList.setAttribute('data-checklist', 'true')
        list.before(currentList)
        currentKind = kind
      }
      currentList.append(item)
    }
    list.remove()
  }

  return container
}

export function markdownToEditorHtml(markdown: string): string {
  return markdownToFormattedNoteContainer(markdown).innerHTML
}

/**
 * Returns the Markdown represented by the editor's rendered block structure.
 * This keeps clipboard output aligned with what the user sees without rewriting
 * the authored body stored on the note.
 */
export function markdownForClipboard(markdown: string): string {
  return serializeRoot(markdownToFormattedNoteContainer(markdown))
}

/** Clone semantic ancestors without asking the browser to inline computed styles. */
function cloneEditorSelection(root: HTMLElement, range: Range): HTMLDivElement | null {
  if (range.collapsed || !root.contains(range.startContainer) || !root.contains(range.endContainer)) return null
  let fragment: Node = range.cloneContents()
  let ancestor = range.commonAncestorContainer
  if (ancestor.nodeType !== Node.ELEMENT_NODE) ancestor = ancestor.parentNode!
  while (ancestor !== root) {
    const wrapper = ancestor.cloneNode(false) as HTMLElement
    wrapper.append(fragment)
    if (wrapper.tagName === 'LI') {
      const checkbox = (ancestor as HTMLElement).querySelector(':scope > input[type="checkbox"]')
      if (checkbox && !wrapper.querySelector(':scope > input[type="checkbox"]')) {
        wrapper.prepend(checkbox.cloneNode(true))
      }
    }
    fragment = wrapper
    ancestor = ancestor.parentNode!
  }
  const container = document.createElement('div')
  container.append(fragment)
  const sourceItems = [...root.querySelectorAll('li')].filter((item) => range.intersectsNode(item))
  for (const [index, item] of [...container.querySelectorAll('li')].entries()) {
    const checkbox = sourceItems[index]?.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')
    if (!checkbox) continue
    item.querySelector(':scope > input[type="checkbox"]')?.remove()
    item.prepend(checkbox.cloneNode(true))
  }
  return container
}

/** Keep task markers even when a native selection starts inside a task's text. */
export function checklistSelectionForClipboard(root: HTMLElement, range: Range): string | null {
  const container = cloneEditorSelection(root, range)
  if (!container?.querySelector('input[type="checkbox"], ul[data-checklist]')) return null
  return serializeRoot(container)
}

export function editorSelectionForClipboard(
  root: HTMLElement,
  range: Range,
  selectedText = range.toString(),
): { text: string; html?: string } | null {
  const container = cloneEditorSelection(root, range)
  if (!container) return null
  // Chrome's native clipboard HTML includes the editor's painted background.
  // Export authored semantics instead: MARK is a highlight; theme colors are not.
  for (const element of container.querySelectorAll('[style], [class], [id]')) {
    element.removeAttribute('style')
    element.removeAttribute('class')
    element.removeAttribute('id')
  }
  if (container.querySelector('input[type="checkbox"], ul[data-checklist]')) {
    // HTML-first receivers commonly discard checkbox inputs.
    return { text: serializeRoot(container) }
  }
  for (const element of container.querySelectorAll(`[${NOTE_LINK_OPEN_ATTRIBUTE}], [${NOTE_LINK_CARET_ATTRIBUTE}], [${SOFT_LINE_BREAK_PLACEHOLDER_ATTRIBUTE}]`)) {
    element.remove()
  }
  return { text: selectedText.replace(/\u200B/g, ''), html: container.innerHTML }
}

function escapeMarkdownText(text: string): string {
  return text
    .replace(/\u200B/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/([*_`\[\]])/g, '\\$1')
}

function inlineMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return escapeMarkdownText(node.textContent ?? '')
  if (node.nodeType !== Node.ELEMENT_NODE) return ''

  const element = node as HTMLElement
  if (element.hasAttribute(NOTE_LINK_OPEN_ATTRIBUTE) || element.hasAttribute(NOTE_LINK_GENERATED_ATTRIBUTE)) return ''
  const tag = element.tagName
  const contents = () => [...element.childNodes].map(inlineMarkdown).join('')

  if (tag === 'BR') {
    return element.hasAttribute(SOFT_LINE_BREAK_PLACEHOLDER_ATTRIBUTE) ? '' : '\n'
  }
  if (tag === 'STRONG' || tag === 'B') return `**${contents()}**`
  if (tag === 'EM' || tag === 'I') return `_${contents()}_`
  if (tag === 'U') return `<u>${contents()}</u>`
  if (tag === 'MARK') return `==${contents()}==`
  if (tag === 'SPAN') {
    const background = element.style.backgroundColor || element.style.background
    const isTransparent =
      !background ||
      background === 'transparent' ||
      /^rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)$/.test(background)
    return isTransparent ? contents() : `==${contents()}==`
  }
  if (tag === 'DEL' || tag === 'S') return `~~${contents()}~~`
  if (tag === 'CODE' && element.parentElement?.tagName !== 'PRE') {
    return `\`${(element.textContent ?? '').replace(/`/g, '\\`')}\``
  }
  if (tag === 'A') {
    const label = contents() || escapeMarkdownText(element.getAttribute('href') ?? '')
    const href = (element.getAttribute('href') ?? '').replace(/\)/g, '\\)')
    if (!href) return label
    const title = element.getAttribute('title')
    return `[${label}](${href}${title ? ` "${title.replace(/"/g, '\\"')}"` : ''})`
  }
  if (tag === 'IMG') {
    const alt = escapeMarkdownText(element.getAttribute('alt') ?? '')
    const src = (element.getAttribute('src') ?? '').replace(/\)/g, '\\)')
    return src ? `![${alt}](${src})` : alt
  }
  if (tag === 'INPUT') return ''
  return contents()
}

function serializeListItem(item: HTMLLIElement): string {
  const parts: string[] = []
  for (const child of item.childNodes) {
    if (child.nodeType === Node.ELEMENT_NODE && ['UL', 'OL'].includes((child as HTMLElement).tagName)) {
      const nested = serializeBlock(child as HTMLElement)
      if (nested) parts.push(`\n${nested.split('\n').map((line) => `  ${line}`).join('\n')}`)
    } else if (
      child.nodeType === Node.ELEMENT_NODE &&
      BLOCK_TAGS.has((child as HTMLElement).tagName)
    ) {
      parts.push(serializeBlock(child as HTMLElement))
    } else {
      parts.push(inlineMarkdown(child))
    }
  }
  return parts.join('').trim()
}

function serializeList(list: HTMLOListElement | HTMLUListElement): string {
  const items = [...list.children].filter((child): child is HTMLLIElement => child.tagName === 'LI')
  const ordered = list.tagName === 'OL'
  const checklist = list.tagName === 'UL' && list.hasAttribute('data-checklist')
  const start = ordered ? Number.parseInt(list.getAttribute('start') ?? '1', 10) || 1 : 1
  const nonEmptyItems = items
    .map((item) => ({ item, content: serializeListItem(item) }))
    .filter(({ content }) => Boolean(content))

  return nonEmptyItems
    .map(({ item, content }, index) => {
      const checkbox = item.querySelector(':scope > input[type="checkbox"]') as HTMLInputElement | null
      const marker = ordered
        ? `${start + index}. `
        : checkbox || checklist
          ? `- [${checkbox?.checked ? 'x' : ' '}] `
          : '- '
      const [first = '', ...continuation] = content.split('\n')
      return `${marker}${first}${
        continuation.length > 0
          ? `\n${continuation.map((line) => `${' '.repeat(marker.length)}${line}`).join('\n')}`
          : ''
      }`.trimEnd()
    })
    .join('\n')
}

const CODE_LINE_BLOCK_TAGS = new Set(['DIV', 'P'])

function codeBlockText(element: HTMLElement): string {
  let text = ''
  const children = [...element.childNodes]
  for (const [index, child] of children.entries()) {
    if (child.nodeType === Node.TEXT_NODE) {
      text += child.textContent ?? ''
      continue
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue
    const childElement = child as HTMLElement
    if (childElement.tagName === 'BR') {
      text += '\n'
      continue
    }
    const isLineBlock = CODE_LINE_BLOCK_TAGS.has(childElement.tagName)
    if (isLineBlock && text && !text.endsWith('\n')) text += '\n'
    text += codeBlockText(childElement)
    if (isLineBlock && index < children.length - 1 && !text.endsWith('\n')) text += '\n'
  }
  return text
}

function serializeBlock(element: HTMLElement): string {
  const tag = element.tagName
  if (tag === 'HR') return '---'
  if (tag === 'UL' || tag === 'OL') return serializeList(element as HTMLUListElement | HTMLOListElement)
  if (tag === 'PRE') {
    const code = codeBlockText(element).replace(/\n$/, '')
    return `\`\`\`\n${code}\n\`\`\``
  }
  if (tag === 'BLOCKQUOTE') {
    const quote = serializeRoot(element).trim()
    return quote
      .split('\n')
      .map((line) => `> ${line}`.trimEnd())
      .join('\n')
  }
  if (tag === 'DIV') return serializeRoot(element)
  if (/^H[1-6]$/.test(tag)) {
    return `${'#'.repeat(Number(tag[1]))} ${[...element.childNodes].map(inlineMarkdown).join('').trim()}`
  }
  return [...element.childNodes].map(inlineMarkdown).join('').trimEnd()
}

export function serializeRoot(root: ParentNode): string {
  const blocks: string[] = []
  let inline = ''

  const flushInline = () => {
    if (!inline) return
    blocks.push(inline)
    inline = ''
  }

  for (const child of root.childNodes) {
    if (child.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((child as HTMLElement).tagName)) {
      flushInline()
      blocks.push(serializeBlock(child as HTMLElement))
    } else if (child.nodeType === Node.TEXT_NODE && !(child.textContent ?? '').trim()) {
      continue
    } else {
      inline += inlineMarkdown(child)
    }
  }
  flushInline()

  while (blocks[0] === '') blocks.shift()
  while (blocks.at(-1) === '') blocks.pop()
  return blocks.join('\n\n')
}

function unwrapGoogleKeepLabelLinks(root: ParentNode): void {
  for (const link of root.querySelectorAll<HTMLAnchorElement>('a[href]')) {
    const text = (link.textContent ?? '').trim()
    if (!/^#[a-z0-9][a-z0-9_-]*$/i.test(text)) continue
    try {
      const url = new URL(link.getAttribute('href') ?? '', document.baseURI)
      if (url.protocol !== 'https:' || url.hostname !== 'keep.google.com') continue
      const labelRoute = /^#label\/(.+)$/i.exec(url.hash)
      if (!labelRoute) continue
      const label = decodeURIComponent(labelRoute[1] ?? '').trim()
      if (text.slice(1).toLocaleLowerCase() !== label.toLocaleLowerCase()) continue
      link.replaceWith(...link.childNodes)
    } catch {
      // Malformed and non-Keep links remain governed by ordinary URL sanitization.
    }
  }
}

export function pastedHtmlToMarkdown(html: string): string {
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'a',
      'b',
      'blockquote',
      'br',
      'code',
      'del',
      'div',
      'em',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'hr',
      'i',
      'img',
      'input',
      'li',
      'mark',
      'ol',
      'p',
      'pre',
      's',
      'span',
      'strong',
      'u',
      'ul',
    ],
    ALLOWED_ATTR: ['alt', 'checked', 'data-checklist', 'href', 'src', 'start', 'style', 'title', 'type'],
  })
  const container = document.createElement('div')
  container.innerHTML = sanitized
  unwrapGoogleKeepLabelLinks(container)
  unwrapInlineTagLinks(container)
  return serializeRoot(container)
}

export function looksLikeMarkdown(text: string): boolean {
  return /(^|\n)\s*(#{1,6}\s|>|[-*+]\s(?:\[[ xX]\]\s)?|\d+\.\s|```|~~~|(?:-{3,}|\*{3,}))|\*\*\S|_\S|==\S|<u(?:\s[^>]*)?>[\s\S]*?<\/u>|\[[^\]]+\]\([^\s)]+\)/im.test(
    text,
  )
}
